import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton } from '@/shared/ui';

import type { TeamWorkload } from '../model/types';
import { useTeamWorkload } from '../queries/useCommitments';
import styles from './WorkList.module.css';

/**
 * Obciążenie moich zespołów (decyzja 82) — <b>wyłącznie do odczytu</b>.
 *
 * ⭐ **Rola otwiera ekran, członkostwo wyznacza zawartość.** Lider Płatności nie zagląda do
 * Magazynu, mimo że rola jest globalna — zakres liczy serwer z przynależności, a nie front
 * z parametru.
 *
 * ⛔ **Ani jednej akcji.** Lider to nie właściciel i nie ma się nim stawać: zamykanie zobowiązań
 * zostaje przy właścicielu i adminie (spec 9.2). Wiersze prowadzą do elementu — tam decyduje ten,
 * kto za niego odpowiada.
 *
 * ⚠ **Czego ten ekran NIE pokazuje: kto imiennie zalega.** Zobowiązanie dziedziczy właściciela
 * z elementu (spec 3.4), a właścicielem bywa **zespół** — więc wskazanie osoby wymagałoby danych,
 * których model nie ma. Pokazujemy więc **elementy**, nie ludzi; to jest uczciwa odpowiedź na
 * pytanie „gdzie się pali u nas", a nie namiastka odpowiedzi na „kto zawinił".
 */
export function TeamWorkloadScreen() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const workload = useTeamWorkload();

  if (workload.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton width="20rem" height="2rem" announce />
        <Skeleton height="6rem" />
      </div>
    );
  }

  if (workload.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(workload.error)}</p>
        <Button onClick={() => void workload.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t('teamWorkload.title')}</h1>
      </header>

      <p className={styles['hint']}>{t('teamWorkload.lead')}</p>

      {workload.data.length === 0 ? (
        <div className={styles['wrap']}>
          {/* Lider bez zespołu to stan poprawny, nie awaria — i zdanie ma mówić, co z tym zrobić. */}
          <p className={styles['empty']}>{t('teamWorkload.noTeams')}</p>
        </div>
      ) : (
        workload.data.map((team) => <TeamSection key={team.teamId} team={team} />)
      )}
    </div>
  );
}

function TeamSection({ team }: { team: TeamWorkload }) {
  const { t } = useTranslation();

  return (
    <section>
      <header className={styles['head']}>
        <h2 className={styles['title']}>{team.teamName}</h2>
        <span className={styles['count']}>
          {t('teamWorkload.summary', {
            elements: team.elements,
            active: team.active,
            sleeping: team.sleeping,
            draugr: team.draugr,
          })}
        </span>
      </header>

      <div className={styles['wrap']}>
        {team.hottest.length === 0 ? (
          <p className={styles['empty']}>{t('teamWorkload.quiet')}</p>
        ) : (
          <table className={styles['table']}>
            <thead>
              <tr>
                <th scope="col">{t('teamWorkload.column.element')}</th>
                <th scope="col">{t('teamWorkload.column.active')}</th>
                <th scope="col">{t('teamWorkload.column.sleeping')}</th>
                <th scope="col">{t('teamWorkload.column.draugr')}</th>
              </tr>
            </thead>
            <tbody>
              {team.hottest.map((element) => (
                <tr key={element.elementId} className={element.draugr > 0 ? styles['rowDanger'] : ''}>
                  <td className={styles['elementCell']}>
                    {/* Prowadzi na element z otwartym panelem zobowiązań — tam, gdzie właściciel
                        może coś zrobić. Ten ekran tylko wskazuje kierunek. */}
                    <Link
                      className={styles['link']}
                      to={`/constellation/${element.elementId}?panel=commitments&el=${element.elementId}`}
                    >
                      {element.title}
                    </Link>
                  </td>
                  <td>{element.active}</td>
                  <td className={styles['muted']}>{element.sleeping}</td>
                  <td className={element.draugr > 0 ? styles['markDraugr'] : styles['muted']}>
                    {element.draugr}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
